<script lang="ts">
  // Overview > By site — live occupancy per site (who's where, right now).
  // Split out of the old OverviewPage.svelte's "Sites" section; this is
  // presence data from hrOverviewApi, distinct from Sites & devices'
  // SitesPage (geofence CRUD) which chunk A briefly (and wrongly) reused
  // here as a placeholder.
  import { createQuery } from '@tanstack/svelte-query';
  import { ChevronDown } from 'lucide-svelte';
  import { hrOverviewApi } from '$lib/api/hr';
  import { fieldApi } from '$lib/api/field';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import OverviewDateNav from './OverviewDateNav.svelte';
  import { todayISO } from '$lib/utils/appDate';

  // Shares the day picker with Overview > Pulse. "Here now" is a live fact,
  // so on a past day the server sends it back empty and each card falls back
  // to its present-that-day headcount — the honest number for a day that's
  // already over.
  let selectedDate = $state(todayISO());
  const viewingToday = $derived(selectedDate === todayISO());

  const query = createQuery(() => ({
    queryKey: [session.isFieldScoped ? 'field-overview' : 'hr-overview', selectedDate],
    queryFn: () => session.isFieldScoped ? fieldApi.overview(selectedDate) : hrOverviewApi.get(selectedDate),
    refetchInterval: viewingToday ? 60_000 : false
  }));

  function fmtTime(dt: string): string {
    return new Date(dt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  function goToSites() {
    route.push({ tab: 'sites', sub: 'sites' });
  }
</script>

<div class="tym-page">
  <OverviewDateNav date={selectedDate} onChange={(d) => (selectedDate = d)} />

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load site occupancy.</div>
  {:else if query.data}
    {@const o = query.data}
    {@const isToday = o.date === todayISO()}
    {@const noSiteNow = o.no_site_punched_in_now ?? { count: 0, users: [] }}
    {#if o.sites.length === 0 && o.remote_punched_in_now.count === 0 && (o.no_site_punched_in_now?.count ?? 0) === 0}
      <div class="tym-empty-state tym-card">
        <span class="tym-empty-title">No sites configured yet</span>
        Add a geofenced location so employees can punch in on-site —
        <button type="button" class="font-semibold text-navy underline underline-offset-2" onclick={goToSites}
          >add one in Sites &amp; devices</button
        >.
      </div>
    {:else}
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {#each o.sites as site (site.work_zone_id)}
          <details class="tym-disclosure tym-card p-5">
            <summary class="flex cursor-pointer items-start justify-between gap-2">
              <span class="min-w-0">
                <span class="flex items-center gap-2">
                  <span class="tym-section-title truncate text-base">{site.name}</span>
                  {#if site.all_users}
                    <span class="tym-badge tym-badge--info shrink-0">open to all</span>
                  {/if}
                </span>
                <span class="mt-1.5 flex items-center gap-1.5">
                  {#if isToday}
                    <StatusLamp tone={site.occupancy_now.count > 0 ? 'good' : 'idle'} pulse={site.occupancy_now.count > 0} />
                    <span class="tym-stat-value text-2xl">{site.occupancy_now.count}</span>
                    <span class="tym-section-sub">here now</span>
                  {:else}
                    <span class="tym-stat-value text-2xl">{site.present_today_count}</span>
                    <span class="tym-section-sub">present that day</span>
                  {/if}
                </span>
                <p class="tym-section-sub mt-1">
                  {#if isToday}{site.present_today_count} present today &middot; {/if}{site.assigned_count} assigned
                </p>
              </span>
              <ChevronDown class="tym-disclosure-chevron mt-1 h-4 w-4 shrink-0 text-dark-gray" />
            </summary>
            <ul class="mt-3.5 space-y-2.5 text-[13px]">
              {#each site.occupancy_now.users as u (u.user_id)}
                <li class="flex justify-between gap-3 border-b border-toyama-cloudy-gray/60 pb-2.5 last:border-none last:pb-0">
                  <span class="truncate text-dark-gray">{u.name}</span>
                  <span class="shrink-0 font-semibold tabular-nums text-navy">{fmtTime(u.punch_in)}</span>
                </li>
              {:else}
                <li class="tym-empty-state !p-0 text-left">
                  {isToday ? 'No one is here right now.' : 'Who was here at a given moment is not kept — only the day’s headcount.'}
                </li>
              {/each}
            </ul>
          </details>
        {/each}

        {#if isToday}
        <details class="tym-disclosure tym-card p-5">
          <summary class="flex cursor-pointer items-start justify-between gap-2">
            <span class="min-w-0">
              <span class="tym-section-title text-base">Hotels</span>
              <span class="mt-1.5 flex items-center gap-1.5">
                <StatusLamp
                  tone={o.remote_punched_in_now.count > 0 ? 'good' : 'idle'}
                  pulse={o.remote_punched_in_now.count > 0}
                />
                <span class="tym-stat-value text-2xl">{o.remote_punched_in_now.count}</span>
                <span class="tym-section-sub">here now</span>
              </span>
            </span>
            <ChevronDown class="tym-disclosure-chevron mt-1 h-4 w-4 shrink-0 text-dark-gray" />
          </summary>
          <ul class="mt-3.5 space-y-2.5 text-[13px]">
            {#each o.remote_punched_in_now.users as u (u.user_id)}
              <li class="flex justify-between gap-3 border-b border-toyama-cloudy-gray/60 pb-2.5 last:border-none last:pb-0">
                <span class="min-w-0 truncate text-dark-gray">
                  {u.name}{#if u.work_zone_name || u.location_name}
                    <span class="text-navy"> · {u.location_name || u.work_zone_name}</span>
                  {/if}
                </span>
                <span class="shrink-0 font-semibold tabular-nums text-navy">{fmtTime(u.punch_in)}</span>
              </li>
            {:else}
              <li class="tym-empty-state !p-0 text-left">No hotel field technicians punched in right now.</li>
            {/each}
          </ul>
        </details>
        <details class="tym-disclosure tym-card p-5">
          <summary class="flex cursor-pointer items-start justify-between gap-2">
            <span class="min-w-0">
              <span class="tym-section-title text-base">Sales</span>
              <span class="mt-1.5 flex items-center gap-1.5">
                <StatusLamp
                  tone={noSiteNow.count > 0 ? 'good' : 'idle'}
                  pulse={noSiteNow.count > 0}
                />
                <span class="tym-stat-value text-2xl">{noSiteNow.count}</span>
                <span class="tym-section-sub">here now</span>
              </span>
            </span>
            <ChevronDown class="tym-disclosure-chevron mt-1 h-4 w-4 shrink-0 text-dark-gray" />
          </summary>
          <ul class="mt-3.5 space-y-2.5 text-[13px]">
            {#each noSiteNow.users as u (u.user_id)}
              <li class="flex justify-between gap-3 border-b border-toyama-cloudy-gray/60 pb-2.5 last:border-none last:pb-0">
                <span class="truncate text-dark-gray">{u.name}</span>
                <span class="shrink-0 font-semibold tabular-nums text-navy">{fmtTime(u.punch_in)}</span>
              </li>
            {:else}
              <li class="tym-empty-state !p-0 text-left">No sales punches right now.</li>
            {/each}
          </ul>
        </details>
        {/if}
      </div>
    {/if}
  {/if}
</div>
